import { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';

const AuthContext = createContext(null);
const TOKEN_KEY = 'trustlab_token';
const USER_KEY = 'trustlab_user';
// Sticky flag — set to '1' after a successful verify-credentials
// (UHID/Client ID + password) login. Read by the /reports patient
// gate to know whether to skip the UHID-confirmation form on
// subsequent visits. Cleared on logout. Lives in its own key so a
// stale `user` snapshot from before the password feature can't
// accidentally satisfy the check.
const UHID_VERIFIED_KEY = 'trustlab_uhid_verified';

const readUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => readUser());
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [uhidVerified, setUhidVerifiedState] = useState(
    () => localStorage.getItem(UHID_VERIFIED_KEY) === '1'
  );

  // Keep both tabs in sync if user logs out/in elsewhere
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === TOKEN_KEY) setToken(e.newValue);
      if (e.key === USER_KEY) setUser(readUser());
      if (e.key === UHID_VERIFIED_KEY) {
        setUhidVerifiedState(e.newValue === '1');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Force-logout when any RTK Query call returns 401 (e.g. token expired,
  // or admin deactivated this account mid-session). The baseQuery wrapper
  // already cleared localStorage; we just need to drop the React state.
  useEffect(() => {
    const onUnauthorized = () => {
      setToken(null);
      setUser(null);
      setUhidVerifiedState(false);
    };
    window.addEventListener('trustlab:unauthorized', onUnauthorized);
    return () => window.removeEventListener('trustlab:unauthorized', onUnauthorized);
  }, []);

  // Impersonation handoff from the admin panel.
  // When an admin clicks "Sign in as user" in /admin/users, the admin
  // app opens this site in a new tab with a URL like:
  //   https://mytrustlab.com/#_imp=<base64 JSON {token, user}>
  // We pick that up on mount, install the session, then immediately
  // strip the hash via replaceState so the token doesn't survive in
  // browser history. JWT is 1h short-lived + carries an `imp_by`
  // claim so the audit trail still works.
  useEffect(() => {
    const hash = window.location.hash || '';
    const m = hash.match(/[#&]_imp=([^&]+)/);
    if (!m) return;
    try {
      const payload = JSON.parse(atob(decodeURIComponent(m[1])));
      if (payload?.token && payload?.user) {
        localStorage.setItem(TOKEN_KEY, payload.token);
        localStorage.setItem(USER_KEY, JSON.stringify(payload.user));
        // Impersonation sessions skip the UHID gate — admin is dropping
        // straight into the user's surface, mirroring a verified login.
        localStorage.setItem(UHID_VERIFIED_KEY, '1');
        setToken(payload.token);
        setUser(payload.user);
        setUhidVerifiedState(true);
      }
    } catch (e) {
      // Malformed hash — ignore.
    } finally {
      // Always clean the URL so the token never survives in history.
      const cleanHash = hash.replace(/[#&]?_imp=[^&]*/, '');
      window.history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search + (cleanHash === '#' ? '' : cleanHash),
      );
    }
  }, []);

  // Persists session after a successful login or signup. The optional
  // `uhidVerified` flag should be `true` only when the auth flow
  // proved the user knows their UHID/Client ID + password (i.e. came
  // through verify-credentials). OTP-only logins / signups should
  // omit it (or pass false) so /reports still asks for confirmation
  // until they go through the credentials path.
  const setSession = useCallback(({ token: t, user: u, uhidVerified: v }) => {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
    setToken(t || null);
    setUser(u || null);
    if (v === true) {
      localStorage.setItem(UHID_VERIFIED_KEY, '1');
      setUhidVerifiedState(true);
    } else if (v === false) {
      localStorage.removeItem(UHID_VERIFIED_KEY);
      setUhidVerifiedState(false);
    }
    // v === undefined → leave the existing flag alone (used by paths
    // that just want to refresh the user/token without touching it).
  }, []);

  // Update only the cached user (e.g. after profile edit) — keep the token.
  const updateUser = useCallback((u) => {
    if (!u) return;
    localStorage.setItem(USER_KEY, JSON.stringify(u));
    setUser(u);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(UHID_VERIFIED_KEY);
    setToken(null);
    setUser(null);
    setUhidVerifiedState(false);
  }, []);

  // Derived helpers. `isDoctor` flips for the doctor-mode session —
  // the doctor login fork mints a User row with user_type='doctor'
  // and ships a `doctor` snapshot (name, specialization, hospital,
  // doctor_code) inside the user object so the portal can render
  // "Dr. X" branding without an extra lookup.
  const isDoctor = user?.user_type === 'doctor';
  const doctor   = isDoctor ? (user?.doctor || null) : null;

  const value = useMemo(
    () => ({
      user,
      token,
      uhidVerified,
      isDoctor,
      doctor,
      setSession,
      updateUser,
      logout,
      isAuthenticated: !!token,
    }),
    [user, token, uhidVerified, isDoctor, doctor, setSession, updateUser, logout]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
